import styled from '@emotion/styled';
import Link from 'next/link';

import { Paragraph } from '@/components/common/MarkUp';
import { Stack } from '@/components/layout/Stack';

import type { Audience } from '@/sanity/sanity.types';

export const programmePath = (
  region: string,
  slug: string,
  audienceRef: Audience,
) => {
  switch (region) {
    case 'en-GB':
      return `${audienceRef ? audienceRef.basePath : ''}/programmes/${slug}`;
    case 'en-US':
      return `${audienceRef ? audienceRef.basePath : ''}/programs/${slug}`;
    default:
      return `${audienceRef ? audienceRef.basePath : ''}/programmes/${slug}`;
  }
};

export interface ProgrammeLinksProp {
  programmes: {
    name: string;
    slug: {
      current: string;
    };
    region: string;
    audienceRef: Audience;
  }[];
}

export const ProgrammeLinks = ({ programmes }: ProgrammeLinksProp) => {
  if (!programmes || programmes.length === 0) return null;

  return (
    <Stack spacing="--space-component-sm">
      <StyledParagraph size="label-sm">Programmes</StyledParagraph>
      <ProgrammesList size="body-md">
        {programmes?.map((programme) => {
          return (
            <Programme
              key={programme.name}
              href={programmePath(
                programme.region,
                programme.slug.current,
                programme.audienceRef,
              )}
            >
              {programme.name}
            </Programme>
          );
        })}
      </ProgrammesList>
    </Stack>
  );
};

const StyledParagraph = styled(Paragraph)`
  color: var(--text-warm-subtle);
`;

const ProgrammesList = styled(Paragraph)`
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const Programme = styled(Link)`
  display: contents;
  text-decoration: none;
  color: var(--text-action);

  :not(:last-of-type)::after {
    content: '';
    display: inline-block;
    background: var(--icon-warm-subtle);
    height: 4px;
    width: 4px;
    border-radius: 50%;
    margin: 0 6px;
    top: -4px;
    position: relative;
  }
`;
